// Drupal taxonomy term ids for the UTC project fields that admins can edit
// inline in the report tables (ProjectReportTable.vue, MarketOverview.vue).
// Single source of truth for the ids posted by updateUtcFields() — the `text`
// is what the table shows, the `value` is what the backend stores.

// Selling / leasing lifecycle, split by segment. Commented-out entries are
// taxonomy terms that exist but are intentionally not offered in the editors.
export const STRATA_STATUS_OPTIONS = [
  { text: "Now Selling", value: 2882 },
  { text: "Sold Out", value: 2978 },
  { text: "Now Selling 2", value: 21135 },
  { text: "Sold Out 2", value: 11514 },
];

export const RENTAL_STATUS_OPTIONS = [
  { text: "Now Leasing", value: 11128 },
  { text: "Leased Out", value: 11126 },
  { text: "Now Leasing 2", value: 21181 },
  { text: "Leased Out 2", value: 21182 },
];

// Every term in the `project_status` vocabulary, keyed by term id, for rendering
// a stored status back as a label. This is the read side — the OPTIONS lists
// above are the write side and deliberately omit terms that are not offered in
// the editors (e.g. TBA), so they cannot be used to resolve an arbitrary id.
export const PROJECT_STATUS_LABELS = {
  2880: "In Planning",
  2881: "Selling Soon",
  2882: "Now Selling",
  21135: "Now Selling 2",
  2978: "Sold Out",
  11514: "Sold Out 2",
  2976: "Leasing Soon",
  11128: "Now Leasing",
  21181: "Now Leasing 2",
  11126: "Leased Out",
  21182: "Leased Out 2",
  16712: "TBA",
};

// Label for a project-status term id. The id reaches the frontend as a number
// from some endpoints and a string from others, so it is normalized before
// lookup — comparing against string literals silently missed every numeric id.
// Returns "" for an unknown or empty id, which callers render as a dash.
export const projectStatusLabel = (tid) => {
  if (tid === null || tid === undefined || tid === "") return "";
  const key = String(tid).trim();
  return Object.prototype.hasOwnProperty.call(PROJECT_STATUS_LABELS, key)
    ? PROJECT_STATUS_LABELS[key]
    : "";
};

// Pre-sale statuses. Only offered where the table actually lists those rows
// (Market Overview includes In Planning / Selling Soon / Leasing Soon).
export const STRATA_PRE_SALE_STATUS_OPTIONS = [
  { text: "In Planning", value: 2880 },
  { text: "Selling Soon", value: 2881 },
];

export const RENTAL_PRE_SALE_STATUS_OPTIONS = [
  { text: "In Planning", value: 2880 },
  { text: "Leasing Soon", value: 2976 },
];

// Statuses that belong to neither segment in particular: In Planning is one
// taxonomy term shared by strata and rental projects (which is why it appears in
// both pre-sale lists above), and TBA is the catch-all for a project whose
// lifecycle is not known yet.
//
// Offered as their own group by the Bulk Update modal, where a selection can span
// both segments and repeating In Planning under each heading only asks the admin
// to choose between two identical entries. The per-segment lists stay as they
// are: the inline cell editor shows one segment's statuses at a time, so there
// In Planning has to be inside the segment's own list to be reachable at all.
export const SHARED_STATUS_OPTIONS = [
  { text: "In Planning", value: 2880 },
  { text: "TBA", value: 16712 },
];

export const GENERAL_UNIT_TYPE_OPTIONS = [
  { text: "Residential", value: 11153 },
  { text: "Market Strata", value: 2952 },
  { text: "Market Rental", value: 2954 },
  { text: "Below-Market Strata", value: 2953 },
  { text: "Below-Market Rental", value: 2955 },
  { text: "Fee-Simple", value: 2975 },
  // { text: "Senior Housing", value: 11181 },
  // { text: "Co Housing", value: 11148 },
  // { text: "Student Housing", value: 21590 },
  { text: "Commercial", value: 3482 },
  { text: "Office", value: 3031 },
  { text: "Retail", value: 2960 },
  // { text: "Assembly", value: 11239 },
  // { text: "Child Care", value: 3492 },
  // { text: "Community Centre", value: 3517 },
  // { text: "Education Centre", value: 3494 },
  // { text: "Medical", value: 11242 },
  // { text: "Hotel", value: 3495 },
  // { text: "Banquet Hall", value: 11188 },
  // { text: "Supportive Housing", value: 3534 },
  // { text: "Industrial", value: 3515 },
  // { text: "Heavy Industrial", value: 11185 },
  // { text: "Light Industrial", value: 11184 },
  // { text: "Manufacturing", value: 11235 },
  // { text: "Storage", value: 11183 },
  // { text: "Warehouse", value: 11182 },
];

// Every general-unit-type term the Market Overview Unit Type filter offers,
// keyed by the leaf-id suffix that filter uses ("ut_Senior Housing" → "Senior
// Housing"). Separate from GENERAL_UNIT_TYPE_OPTIONS above: that list is the
// inline editor's *write* side and deliberately offers only a handful of terms,
// while this one has to cover every leaf the filter tree can select.
//
// These are scope ids, not display values. `unittype[]` on the projects request
// is what decides whether a UTC is in the dataset at all — the backend selects
// on field_general_unit_type IN (…) (end_points.inc, get_projects_utc_admin) —
// so filtering the panel down to a type that is not in that list can only ever
// match nothing, however the rows are compared afterwards.
//
// Key by the leaf-id suffix, not the display label: the "Restaurant" leaf shows
// as "Food & Beverage" but its id and the taxonomy term are both "Restaurant".
export const GENERAL_UNIT_TYPE_TIDS = {
  // Residential (parent 11153)
  "Market Strata": 2952,
  "Market Rental": 2954,
  "Fee-Simple": 2975,
  "Below-Market Strata": 2953,
  "Below-Market Rental": 2955,
  "Senior Housing": 11181,
  "Co Housing": 11148,
  "Student Housing": 21590,
  "Faculty Housing": 25770,
  "Supportive Housing": 3534,
  // Commercial (parent 3482)
  Office: 3031,
  Retail: 2960,
  Hotel: 3495,
  Restaurant: 25864,
  "Banquet Hall": 11188,
  // Industrial (parent 3515)
  "Heavy Industrial": 11185,
  "Light Industrial": 11184,
  Manufacturing: 11235,
  Storage: 11183,
  Warehouse: 11182,
  // Institutional (parent 25866)
  "Child Care": 3492,
  "Community Centre": 3517,
  "Education Centre": 3494,
  Medical: 11242,
};

// The term ids behind a list of Unit Type leaf suffixes, for `unittype[]`.
// Unknown labels are dropped rather than passed through — an unresolved value
// on the scope query would widen the dataset to whatever the backend makes of
// it, which is worse than the filter simply not narrowing.
export const generalUnitTypeTids = (labels) =>
  (labels || [])
    .map((label) => GENERAL_UNIT_TYPE_TIDS[label])
    .filter((tid) => tid !== undefined);

// The term ids behind a list of Building Type leaf suffixes, for `type[]`.
// Only the labels in `only` resolve — the rest are already on the scope query
// by default, and "Podium" alone has three ids, so resolving it here would widen
// the dataset. Unknown labels are dropped.
export const projectTypeTids = (labels, only) => {
  const tidOf = new Map();
  PROJECT_TYPE_OPTIONS.forEach((o) => {
    if (!tidOf.has(o.text)) tidOf.set(o.text, o.value);
  });
  return (labels || [])
    .filter((label) => only.includes(label))
    .map((label) => tidOf.get(label))
    .filter((tid) => tid !== undefined);
};

// Several labels map to more than one term id ("Podium" has three). Editors
// list each label once, so the first matching id wins on save.
export const PROJECT_TYPE_OPTIONS = [
  { text: "Apartment", value: 2886 },
  { text: "Podium", value: 17174 },
  { text: "Mixed-Use Apartment", value: 2884 },
  { text: "Podium", value: 17175 },
  { text: "Townhome", value: 2870 },
  { text: "Stacked Townhome", value: 3479 },
  { text: "Podium", value: 5910 },
  { text: "Rowhome", value: 6240 },
  { text: "Commercial", value: 2962 },
  { text: "Industrial", value: 3047 },
  { text: "Semi-Detached", value: 3501 },
  { text: "Multiplex", value: 17170 },
  { text: "Duplex", value: 3502 },
  { text: "Triplex", value: 3503 },
  { text: "Fourplex", value: 3504 },
  { text: "Coach House", value: 3505 },
  { text: "Single Family", value: 3506 },
];

// Project Lead — the staff member a UTC is assigned to. A taxonomy reference
// (the project_lead vocabulary), so the ids are what `field_project_lead`
// stores. Shared by the Update UTC modal, the Project Report row editor and the
// Market Overview editors. Only a super admin may set or change it.
export const PROJECT_LEAD_OPTIONS = [
  { text: "Daneca", value: 23488 },
  { text: "Franco", value: 23489 },
  { text: "Jan", value: 23901 },
  { text: "Jen", value: 23485 },
  { text: "Jericho", value: 23494 },
  { text: "John", value: 23484 },
  { text: "Josh", value: 26518 },
  { text: "Jypzy", value: 23490 },
  { text: "Lorraine", value: 23487 },
  { text: "Ninotchka", value: 26519 },
];

// One colour per project lead, keyed by term id. Every fill takes white text at
// 4.5:1 or better, so the same colour works in light and dark mode.
export const PROJECT_LEAD_COLORS = {
  23488: "#B91C1C", // Daneca    red
  23489: "#C2410C", // Franco    orange
  23901: "#A16207", // Jan       amber
  23485: "#15803D", // Jen       green
  23494: "#0F766E", // Jericho   teal
  23484: "#0369A1", // John      sky
  26518: "#1D4ED8", // Josh      blue
  23490: "#6D28D9", // Jypzy     violet
  23487: "#A21CAF", // Lorraine  fuchsia
  26519: "#BE185D", // Ninotchka pink
};

// Neutral fill for a lead that is not in the list any more (a project still
// assigned to someone who has since been removed).
export const PROJECT_LEAD_FALLBACK_COLOR = "#475569";

// The colour for a lead given its term id or its name. Falls back to a neutral
// slate so a stale or unknown lead still renders.
export const projectLeadColor = (idOrName) => {
  if (idOrName === null || idOrName === undefined || idOrName === "") {
    return PROJECT_LEAD_FALLBACK_COLOR;
  }
  const byId = PROJECT_LEAD_COLORS[idOrName];
  if (byId) return byId;
  const wanted = String(idOrName).trim().toLowerCase();
  const hit = PROJECT_LEAD_OPTIONS.find(
    (o) => String(o.value) === wanted || o.text.toLowerCase() === wanted
  );
  return hit ? PROJECT_LEAD_COLORS[hit.value] : PROJECT_LEAD_FALLBACK_COLOR;
};

// Product Type is a plain text field on the backend, not a taxonomy reference.
export const PRODUCT_TYPE_OPTIONS = [
  { text: "Concrete", value: "Concrete" },
  { text: "Wood Frame", value: "Wood Frame" },
  { text: "Townhome", value: "Townhome" },
  { text: "Stacked Townhome", value: "Stacked Townhome" },
  { text: "Mass Timber", value: "Mass Timber" },
  { text: "CLT", value: "CLT" },
  { text: "Multiplex", value: "Multiplex" },
  { text: "Rowhome", value: "Rowhome" },
];

// Unique labels for a Tabulator list editor, in catalogue order.
export const optionLabels = (options) => [
  ...new Set(options.map((option) => option.text)),
];

// Resolves a label back to the term id posted on save.
export const optionValue = (options, label) => {
  const match = options.find((option) => String(option.text) === String(label));
  return match ? match.value : null;
};

// Every term on `field_construction_status_ref`, in construction order — the
// options offered when adding or editing a Construction Photo Timeline entry.
// api/construction_timeline/statuses returns the same list from the vocabulary
// and takes precedence when it answers; this is the offline default so the
// editor still works before that endpoint is deployed.
export const CONSTRUCTION_STATUS_OPTIONS = [
  { tid: 23496, name: "None" },
  { tid: 2899, name: "Pre-Construction" },
  { tid: 25872, name: "Site Cleared" },
  { tid: 2900, name: "In Process" },
  { tid: 3630, name: "Excavation & Foundation" },
  { tid: 3632, name: "Framing" },
  { tid: 3772, name: "Interior Finishing" },
  { tid: 2904, name: "Some Homes Complete" },
  { tid: 2905, name: "Complete" },
];

// Admin-only tracking markers on a UTC, shown and edited in the Market Overview
// grid. `key` is the row property, `field` the Drupal field saved through
// api/utc_basic_fields/save (the backend's presale_tracking_fields() enforces
// the same options). An empty value is "not set" and renders as a dash. When
// every one is "done" and the UTC's latest approved Project History entry is
// approved, the backend empties all four.
const DONE_PENDING = [
  { text: "Done", value: "done" },
  { text: "Pending", value: "pending" },
];

export const TRACKING_FIELDS = [
  {
    key: "spot_price",
    field: "field_utc_spot_price",
    title: "Spot Price",
    options: [
      { text: "Done", value: "done" },
      { text: "Some", value: "some" },
      { text: "Pending", value: "pending" },
    ],
  },
  {
    key: "sl_unit_confirmation",
    field: "field_utc_sl_unit_confirmation",
    title: "S/L Unit Confirmation",
    options: DONE_PENDING,
  },
  {
    key: "lease_sold_pct",
    field: "field_utc_lease_sold_pct",
    title: "Lease/Sold %",
    options: DONE_PENDING,
  },
  {
    key: "bl_incentives",
    field: "field_utc_bl_incentives",
    title: "B/L Incentives",
    options: DONE_PENDING,
  },
];
